'use client';

import React, {createContext, useContext, useEffect, useState} from 'react';
import {checkUserRole} from '#utils/cookies';
import {
  useOrganisationSettings,
  useMerchantSettings
} from '#hooks/queries/useSettings';

interface UserInfo {
  name: string;
  contactName: string;
  avatar?: string;
  role?: string;
}

interface UserContextType {
  userInfo: UserInfo | null;
  userRole: string | null;
  isLoading: boolean;
  resetContext: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({children}: {children: React.ReactNode}) {
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get user role from cookies
  useEffect(() => {
    const role = checkUserRole();
    setUserRole(role);
  }, []);

  // Fetch organisation settings for organisation users
  const {data: orgSettings, isLoading: isOrgLoading} = useOrganisationSettings({
    enabled: userRole === 'organisation'
  });

  // Fetch merchant settings for merchant users
  const {data: merchantSettings, isLoading: isMerchantLoading} =
    useMerchantSettings({
      enabled: userRole === 'merchant'
    });

  // Update user info when settings are loaded
  useEffect(() => {
    if (!userRole) {
      setIsLoading(true);
      return;
    }
    const stillLoadingSettings =
      (userRole === 'organisation' && isOrgLoading) ||
      (userRole === 'merchant' && isMerchantLoading);

    if (stillLoadingSettings) {
      setIsLoading(true);
      return;
    }

    // Settings are loaded (or not needed), update user info
    let name = userRole === 'organisation' ? 'Organization' : 'Merchant';
    let contactName = 'User';
    let avatar: string | undefined;

    if (userRole === 'organisation' && orgSettings?.data) {
      name = orgSettings.data.business_name || 'Organization';
      contactName = orgSettings.data.business_info?.first_name || 'User';
      avatar = orgSettings.data.business_info?.avatar;
    } else if (userRole === 'merchant' && merchantSettings?.data) {
      name =
        merchantSettings.data.business_name ||
        merchantSettings.data.name ||
        'Merchant';
      contactName =
        merchantSettings.data.business_info?.first_name ||
        merchantSettings.data.contact_person?.first_name ||
        'User';
      avatar = merchantSettings.data.business_info?.avatar;
    }

    setUserInfo({
      name,
      contactName,
      avatar,
      role: userRole
    });

    // Set loading to false only after userInfo is set with actual data
    setIsLoading(false);
  }, [
    userRole,
    orgSettings,
    merchantSettings,
    isOrgLoading,
    isMerchantLoading
  ]);

  const resetContext = () => {
    setUserRole(null);
    setUserInfo(null);
  };

  return (
    <UserContext.Provider
      value={{
        userInfo,
        userRole,
        isLoading,
        resetContext
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
